import { useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useAuth } from "./use-auth";
import { useMembership } from "@/modules/membership";
import { useBrands } from "@/modules/brand";
import type { Permission, UserRole } from "../permissions";
import { hasPermission, hasAnyPermission, hasAllPermissions, hasMinimumRole, canPerformAction } from "../permissions";

export interface UsePermissionReturn {
	/**
	 * Current user's role in the active workspace
	 */
	role: UserRole | null;

	/**
	 * Check if user has a specific permission
	 */
	can: (permission: Permission) => boolean;

	/**
	 * Check if user has any of the specified permissions
	 */
	canAny: (permissions: Permission[]) => boolean;

	/**
	 * Check if user has all of the specified permissions
	 */
	canAll: (permissions: Permission[]) => boolean;

	/**
	 * Check if user has at least the specified role level
	 */
	hasRole: (requiredRole: UserRole) => boolean;

	/**
	 * Check if user can perform action (with optional ownership check)
	 */
	canPerform: (permission: Permission, isOwner?: boolean) => boolean;

	/**
	 * Check if user is admin
	 */
	isAdmin: boolean;

	/**
	 * Loading state for membership data
	 */
	loading: boolean;
}

/**
 * Hook to check permissions for the current user in the active workspace
 */
export function usePermission(): UsePermissionReturn {
	const params = useParams<{ brandUrl: string }>();
	const { user, currentWorkspaceId, loading: authLoading } = useAuth();
	const { memberships } = useMembership();
	const { brands } = useBrands();

	// Use URL parameter as primary source of truth, fallback to auth store
	// This ensures immediate updates on navigation without waiting for async state
	const workspaceId = params.brandUrl || currentWorkspaceId;

	// Cache the last confirmed role per workspace to prevent flickering during transitions
	const rolesCacheRef = useRef<Map<string, UserRole>>(new Map());

	// Find user's membership for current workspace
	const currentMembership = useMemo(() => {
		if (!workspaceId || !memberships || !user) return null;

		return memberships.find((m) => m.workspace_id === workspaceId && m.user_id === user.id);
	}, [workspaceId, memberships, user]);

	// Find role from brands data (brands already include user's role for each workspace)
	const roleFromBrands = useMemo(() => {
		if (!workspaceId || !brands) return null;

		const brand = brands.find((b) => b.id === workspaceId);
		return (brand?.role as UserRole) || null;
	}, [workspaceId, brands]);

	// Determine the role using multiple sources with priority:
	// 1. Membership data (most authoritative when available)
	// 2. Brands data (available immediately, includes role)
	// 3. Cached role (fallback during transitions)
	const role = useMemo(() => {
		if (!workspaceId) return null;

		// First priority: membership data
		const membershipRole = (currentMembership?.role as UserRole) || null;
		if (membershipRole) {
			rolesCacheRef.current.set(workspaceId, membershipRole);
			return membershipRole;
		}

		// Second priority: brands data (already loaded in sidebar)
		if (roleFromBrands) {
			rolesCacheRef.current.set(workspaceId, roleFromBrands);
			return roleFromBrands;
		}

		// Third priority: cached role from previous visit
		const cachedRole = rolesCacheRef.current.get(workspaceId);
		if (cachedRole) {
			return cachedRole;
		}

		return null;
	}, [currentMembership?.role, roleFromBrands, workspaceId]);

	// Only consider loading if:
	// 1. Auth is still loading, OR
	// 2. We don't have a role yet AND brands haven't been loaded
	// Note: We don't wait for memberships since editors don't have access to that endpoint
	const loading = authLoading || (!role && !brands);

	return {
		role,
		loading,

		can: (permission: Permission) => hasPermission(role, permission),

		canAny: (permissions: Permission[]) => hasAnyPermission(role, permissions),

		canAll: (permissions: Permission[]) => hasAllPermissions(role, permissions),

		hasRole: (requiredRole: UserRole) => hasMinimumRole(role, requiredRole),

		canPerform: (permission: Permission, isOwner?: boolean) => canPerformAction(role, permission, isOwner),

		isAdmin: role === "admin",
	};
}
